import { useContext, useEffect, useState } from 'react';
import axios from "axios";
import Navbar from '../Components/Navbar';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import FeedbackPopup from '../Common/FeedbackPopup';
import { toast } from 'react-toastify';
import WhythisApp from './WhythisApp';
import HowItWorks from './HowItWorks';
import FAQ from './FAQ';
import UseCases from './UseCases';
import { UserContext } from '../Context/UserContext';
import { sentryLog } from '../Utils/SentryLog';
import FooterComponent from '../Common/FooterComponent';
import { getLqzyParams } from '../Utils/GetLqzyParams';
import { langs } from '../Common/LanguageData';
import PdfIcon from '../Icons/PdfIcon';
/* global gtag */

const convertToBase64 = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]); // Extract Base64
    reader.readAsDataURL(file);
});

const cleanExtractedText = (text) => {
  return text
    .replace(/^Sure,?\s+here\s+(?:is|['’]?s)\s.*?:\s*/i, '')
    .replace(/^[\s\S]*?extracted text:\s*/i, '') 
    .replace(/^```(plaintext|text)?\s*/i, '') 
    .replace(/(\n)?```$/, '') 
    .replace(/\n{3,}/g, '\n\n') 
    .trim();
};

const headlineArray = ["Save hours of manual typing - Extract text from PDF or image instantly", "No more typing - Get text from image or PDF in seconds", "Tired of retyping scanned docs? Get text from image or PDF in 1 click"];

const mobileHeadlineArray = ["Scan text from image or PDF in seconds", "Get text from image or PDF. Instantly.", "Extract text from PDF or image in 1 click", "Extract text from PDF or image in seconds"];

const randomIdx = Math.floor(Math.random() * headlineArray.length); // gives 0, 1, or 2
const mobileRandomIdx = Math.floor(Math.random() * mobileHeadlineArray.length); // gives 0, 1 2, upto length-1


function Home() {

  const [imgFile, setImgFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [base64Image, setBase64Image] = useState("");
  const [extractedText, setExtractedText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isCameraCapturedImg, setIsCameraCapturedImg] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [cameraStream, setCameraStream] = useState(null);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [selectedLang, setSelectedLang] = useState("");
  const [selectedPdfPageNo, setSelectedPdfPageNo] = useState(1);

  const [disableExtraction, setDisableExtraction] = useState(false);

  const { user } = useContext(UserContext);

  const isUserLoggedIn = user ? true : false;
  const isPremiumUser = user ? user.isPremium : false;
  const isTextOcrFeedbackSubmitted = user?.isTextOcrFeedbackSubmitted;

  const isShowConsole = process.env.NODE_ENV === 'development';

  const location = useLocation();
  const navigate = useNavigate();

  const API_BASE_URL = process.env.NODE_ENV === 'development' 
    ? process.env.REACT_APP_LOCAL_API_URL 
    : process.env.REACT_APP_API_URL;

  useEffect(() => {

    //
    const referrer = document.referrer;
    sentryLog(`User coming in.. referred by -> ${referrer}`);
    //
    
    let planAmount = null;
    let planType = null;
    let pendingPaypalPlanLink = null;
    let pendingSheetsPlanLink = null;

    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('source');
    if (source && source.startsWith('extension')) {
      document.body.innerHTML = "<p>Login successful. Closing tab...</p>";
      setTimeout(() => window.close(), 1500);
    }

    try {
      planAmount = localStorage.getItem("pendingPlanAmount");
      planType = localStorage.getItem("pendingPlanType");
      pendingPaypalPlanLink = localStorage.getItem("pendingPaypalPlanLink");
      pendingSheetsPlanLink = localStorage.getItem("pendingSheetsPlanLink");
    } catch (err) {
      console.error("LocalStorage access blocked:", err);
      sentryLog(`Home.js - LocalStorage access blocked: ${err?.message || err}`);
    }

    if ((planAmount && planType) || pendingPaypalPlanLink) {
      openPricingPage();
    }
    if(pendingSheetsPlanLink){
      openSheetsPricingPage();
    }

    try{
      scrollToHashTarget();
    }catch(err){
      isShowConsole && console.log('error in hash scrolling logic in home.js: ', err);
    }
  }, []);

  useEffect(() => {
    if (!extractedText || !extractedText.trim()) return;

    const blob = new Blob([extractedText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    
    setBlobUrl(url);

    return () => URL.revokeObjectURL(url);

  }, [extractedText]);

  useEffect(() => {
      return () => {
          if (cameraStream) {
              cameraStream.getTracks().forEach(track => track.stop());
              setCameraStream(null);
          }
      };
  }, [cameraStream]);

  useEffect(() => {
    let disableExtractButton = extractedText || isLoading;
    setDisableExtraction(disableExtractButton);
  }, [extractedText, isLoading])

  function scrollToHashTarget(retries = 10) {
    const id = window.location.hash?.substring(1);
    if (!id) return;

    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else if (retries > 0) {
      setTimeout(() => scrollToHashTarget(retries - 1), 200); // try again
    }
  }

  const openFeedbackPage = e => {
    try{
        const query = getLqzyParams();
        navigate(`/feedback${query}`);
    }catch(err){
        isShowConsole && console.error('error in openFeedbackPage method: ', err);
        sentryLog(`Home.js - openFeedbackPage failed: ${err?.message || err}`);
    }
  }

  const openPricingPage = e => {
    try{
        const query = getLqzyParams();
        navigate(`/pricing${query}`);
    }catch(err){
        isShowConsole && console.error('error in openPricingPage method: ', err);
        sentryLog(`Home.js - openPricingPage failed: ${err?.message || err}`);
    }
  }
  
  const openSheetsPricingPage = e => {
    try{
        const query = getLqzyParams();
        navigate(`/sheets-extension-pricing${query}`);
    }catch(err){
        isShowConsole && console.error('error in openSheetsPricingPage method: ', err);
        sentryLog(`Home.js - openSheetsPricingPage failed: ${err?.message || err}`);
    }
  }

  const captureTextFromFile = async(base64val) => {
    try{
      //user search term infos - pass to the backend
      const click = JSON.parse(localStorage.getItem('ads_click') || 'null');
      const searchTerm = click?.search_term || null;
      const keyword    = click?.keyword || null;
      //

      const isPdf = imgFile?.type === 'application/pdf';

      isShowConsole && console.log('ssrk pdf check: ', isPdf, "::", selectedPdfPageNo);

      const res = isPdf ? await axios.post(`${API_BASE_URL}/api/extract`, 
        { base64Pdf: base64val, pageToExtract: selectedPdfPageNo, language: selectedLang, searchTerm: searchTerm, keyword: keyword }, 
        { headers: { "Content-Type": "application/json" }, withCredentials: true }
      ) : await axios.post(`${API_BASE_URL}/api/extract`, 
        { base64Image: base64val, language: selectedLang, searchTerm: searchTerm, keyword: keyword }, 
        { headers: { "Content-Type": "application/json" }, withCredentials: true }
      );

      isShowConsole && console.log('res check: ', res,'::', res.status);

      let extractedText = res.data.extractedText;
      isShowConsole && console.log('extracted text: ', extractedText);
      let refinedExtractedText = cleanExtractedText(extractedText);
      isShowConsole && console.log('cleaned extracted text: ', refinedExtractedText);
      setExtractedText(DOMPurify.sanitize(refinedExtractedText));
      sentryLog('Home.js - Text extraction successful', 'info');
    }catch(err){ 
      isShowConsole && console.log('error in capture method: ', err);
      sentryLog(`Home.js - /api/extract failed: ${err?.message || err}`);

      let errMsg;

      errMsg = 
        !navigator.onLine ? "Please connect to a network and try again" 
        : err.code === "ERR_NETWORK" ? "Server is unreachable. Please try again later." 
        : (err.response?.data?.code === "IMAGE_SIZE_EXCEED") ? err?.response?.data?.error
        : (err.response?.data?.code === "DAILY_LIMIT_REACHED") ? "You've hit your free limit for today 😔" 
        : (err.response?.data?.error) ? err.response.data.error : "Something went wrong, please try again";

      if (err.code === "ERR_NETWORK") {
        try {
          const retryRes = await axios.post(`${API_BASE_URL}/api/extract`, 
            { base64Image: base64val }, 
            { headers: { "Content-Type": "application/json" }, withCredentials: true }
          );
    
          isShowConsole && console.log('retry res check: ', retryRes,'::', retryRes.status);
    
          let extractedText = retryRes.data.extractedText;
          isShowConsole && console.log('retry extracted text: ', extractedText);
          let refinedExtractedText = cleanExtractedText(extractedText);
          isShowConsole && console.log('retry cleaned extracted text: ', refinedExtractedText);
          setExtractedText(DOMPurify.sanitize(refinedExtractedText));
          sentryLog('Home.js - Retry text extraction successful', 'info');
    
          return;
    
        } catch (retryErr) {
          errMsg = "Server is unreachable. Please try again later."
        }
      }
      
      if(err.response && err.response.data && err.response.data.code === "IMAGE_SIZE_EXCEED"){
        errMsg = 
                <div className='limitExceedErr'>
                  <div className='msg'>{errMsg}</div>
                  <div className='upgradeCta'>
                    <div className='upgradeBtn' onClick={openPricingPage}>Upgrade to Pro</div> - Upload upto 30mb! 😎
                  </div>
                </div>
      }

      if(err.response && err.response.data && err.response.data.code === "DAILY_LIMIT_REACHED"){
        errMsg = 
                <>
                  <div className='limitExceedErr'>
                    <div className='msg'>{errMsg}</div>
                    <div className='upgradeCta'>
                      <div className='upgradeBtn' onClick={openPricingPage}>Upgrade to Pro</div> for unlimited extractions!
                    </div>
                  </div>
                  {!isTextOcrFeedbackSubmitted && <div className='feedbackDiv'>
                    <div className='l1'>OR</div>
                    <div className='l2'><div className='cta' onClick={openFeedbackPage}>Submit a quick feedback</div> to get 5 more credits today</div>
                  </div>}
                </>
      }

      setErrMsg(errMsg);
    }finally{
      setIsLoading(false);
    }
  }

  const handleFileChange = async(e) => {

    //event tracker
    gtag('event', 'choose_file_clicked', {
      event_category: 'Button',
      event_label: 'Choose File'
    });
    //

    let file = e.target.files[0];
    if(file){
      if (preview) {
        URL.revokeObjectURL(preview); 
      }
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop()); 
        setCameraStream(null);
      }

      let imgUrl = URL.createObjectURL(file);
      const base64 = await convertToBase64(file);
      setBase64Image(base64);
      setImgFile(file);
      setPreview(imgUrl);
      setExtractedText("");
      setErrMsg("");
      setIsCapturing(false);
      setIsCameraCapturedImg(false);
    }
  }

  const extractTextFromImage = e => {

    //event tracker
    gtag('event', 'extract_clicked', {
      event_category: 'Button',
      event_label: 'Extract Text'
    });
    //

    if(extractedText !== ""){
      setExtractedText("");
    }
    setIsLoading(true);
    setErrMsg("");
    captureTextFromFile(base64Image);
  }

  const openCamera = async(e) => {
    
    //event tracker
    gtag('event', 'open_camera_clicked', {
      event_category: 'Button',
      event_label: 'Open Camera'
    });
    //

    try {
      setPreview(null);
      setImgFile(null);
      setBase64Image("");
      setIsCapturing(true);
      setExtractedText("");
      setErrMsg("");

      let stream;

      try {
        // Trying to open back camera
        stream = await navigator.mediaDevices?.getUserMedia({
          video: { facingMode: { exact: "environment" }, width: 1280, height: 720 }
        });
      } catch (err) {
        // If back camera fails, fall back to front camera
        stream = await navigator.mediaDevices?.getUserMedia({
          video: { facingMode: "user", width: 1280, height: 720 }
        });
      }

      const videoElement = document.getElementById("cameraPreview");

      if (videoElement) {
        videoElement.srcObject = stream;
        videoElement.play?.(); // Ensures camera preview starts
      }
      
      setCameraStream(stream);
    } catch (error) {
      isShowConsole && console.error("Error accessing camera - openCamera method:", error);
      sentryLog(`Home.js - openCamera failed: ${error?.message || error}`);
    }
  }

  const capturePhoto = e => {

    //event tracker
    gtag('event', 'capture_photo_clicked', {
      event_category: 'Button',
      event_label: 'Capture Photo'
    });
    //

    try {
      const videoElement = document.getElementById("cameraPreview");

      if (videoElement) {
        const canvas = document.createElement("canvas");
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        const imageDataURL = canvas.toDataURL("image/png"); 

        setPreview(imageDataURL);
        setBase64Image(imageDataURL.split(",")[1]);
        setIsCapturing(false);
        setIsCameraCapturedImg(true);
      }

      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
      }
      
    } catch (error) {
      isShowConsole && console.error("Error in capturePhoto method:", error);
      sentryLog(`Home.js - capturePhoto failed: ${error?.message || error}`);
    }
  }

  const handleFeedback = e => {
    //event tracker
        gtag('event', 'feedback_clicked', {
          event_category: 'Button',
          event_label: 'Feedback'
      });
    //
    try{
      isShowConsole && console.log('feedback button clicked');
      setShowFeedbackPopup(true);
    }catch(err){
      isShowConsole && console.log('error in handleFeedback method: ', err);
      sentryLog(`Home.js - handleFeedback failed: ${err?.message || err}`);
    }
  }

  const showCopiedToast = e => {
    try{
      let isFeedbackRequested = sessionStorage.getItem('isFeedbackRequested');

      toast.success(
        <div>
          {isFeedbackRequested ? 
              <span className='downloadToast'>Copied to clipboard!</span> 
            : <span className='downloadToast'>
                Copied to clipboard! We'd love your feedback.{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    //event tracker
                        gtag('event', 'download_feedback_clicked', {
                          event_category: 'Button',
                          event_label: 'Feedback'
                      });
                    //
                    e.preventDefault();
                    setShowFeedbackPopup(true);
                  }}
                >
                  How Can We Serve You Better?
                </a>
              </span>
          }
        </div>,
        {className: `downloadToastContainer ${isFeedbackRequested ? 'normal' : ''}`, autoClose: isFeedbackRequested ? 1500 : 5000, closeButton: isFeedbackRequested ? false : true}
      );

      if(!isFeedbackRequested)
        sessionStorage.setItem('isFeedbackRequested', true);
    }catch(err){
      isShowConsole && console.error("error in showDownloadToast method: ", err);
      sentryLog(`Home.js - showDownloadToast failed: ${err?.message || err}`);
    }
  }

  const showDownloadToast = e => {
    try{
      let isFeedbackRequested = sessionStorage.getItem('isFeedbackRequested');

      toast.success(
        <div>
          {isFeedbackRequested ? 
              <span className='downloadToast'>Download started!</span> 
            : <span className='downloadToast'>
                Download started! We'd love your feedback.{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    //event tracker
                        gtag('event', 'download_feedback_clicked', {
                          event_category: 'Button',
                          event_label: 'Feedback'
                      });
                    //
                    e.preventDefault();
                    setShowFeedbackPopup(true);
                  }}
                >
                  How Can We Serve You Better?
                </a>
              </span>
          }
        </div>,
        {className: `downloadToastContainer ${isFeedbackRequested ? 'normal' : ''}`, autoClose: isFeedbackRequested ? 1500 : 5000, closeButton: isFeedbackRequested ? false : true}
      );

      if(!isFeedbackRequested)
        sessionStorage.setItem('isFeedbackRequested', true);
    }catch(err){
      isShowConsole && console.error("error in showDownloadToast method: ", err);
      sentryLog(`Home.js - showDownloadToast failed: ${err?.message || err}`);
    }
  }

  const redirectToExtension = e => {
    try{
      window.open("https://workspace.google.com/marketplace/app/image_to_text_ocr_for_google_sheets/687083288287", "_blank", "noopener,noreferrer");
    }catch(err){
      isShowConsole && console.log('error in redirectToExtension: ', err);
      sentryLog(`Home.js - redirectToExtension failed: ${err?.message || err}`);
    }
  }

  const initiateLogin = async () => {
    //event tracker
        gtag('event', 'login_clicked', {
            event_category: 'Button',
            event_label: 'Login'
        });
    //
    try{
        await fetch(`${API_BASE_URL}/api/session-init`, {
            method: 'GET',
            credentials: 'include'
          });
        const currentPath = `${window.location.pathname}${window.location.search}`;
        window.location.href = `${API_BASE_URL}/auth/google?state=${encodeURIComponent(currentPath)}`;
    }catch(err){
        isShowConsole && console.error('error in initiate login method: ', err);
        sentryLog(`Home.js - initiateLogin failed: ${err?.message || err}`);
    }
  }

  const handleLangChange = (e) => {
    setSelectedLang(e.target.value);
    setDisableExtraction(false);
  };

  const languageOptions = langs.map((lang, idx) => {
    return(
      <>
        <option className="languageOption" value={lang.code}>{lang.name}</option>
      </>
    )
  });
  

  return (
    <div className='parentDiv'>
      {(user !== undefined) && isUserLoggedIn && <div className='mainDiv loggedInUser'>
        <div className="header">
          <Navbar setShowFeedbackPopup={setShowFeedbackPopup} isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} />     
          {(user !== undefined) && <div className='intro'>
              <div className='wrapper'>
                <h1>{headlineArray[randomIdx]}</h1>
                {
                  isPremiumUser ? 
                    <span>
                      <span className='ctaText'>{user.planType === 'Lifetime' ? 'Lifetime plan' : user.planType} active</span> - Enjoy unlimited extractions on images upto 30MB!
                    </span>
                    :
                    <span>
                      <span className='ctaText'>Try 5 extractions free</span> - Click below to choose a file / open camera
                    </span>
                }
              </div>
          </div>}
          {(user !== undefined && !isMobileMenuOpen) && <div className='mobileIntro'>
            <div className='wrapper'>
              <h1>{mobileHeadlineArray[mobileRandomIdx]}</h1>
              {
                isPremiumUser ? 
                  <span>
                    <span className='ctaText'>{user.planType === 'Lifetime' ? 'Lifetime plan' : user.planType} active!</span>
                  </span>
                  :
                  <span>
                    <span className='ctaText'>Try 5 extractions free!</span>
                  </span>
              }
            </div>
          </div>}
        </div>
        <div className='content1'>
          <div className='fileInputCont'>
            <input type="file" accept="image/*, application/pdf" onChange={handleFileChange} />
            <span>/</span>
            <button onClick={openCamera}>Open Camera</button>
          </div>
          <div className='inputOutputCont'>
            <div className='lhs'>
              {preview ? ((imgFile?.type === 'application/pdf') ? <div className='pdfPreview'><PdfIcon/><span className='pdfName'>{imgFile?.name}</span></div> : <img className={isCameraCapturedImg ? 'cameraCapturedImg' : 'uploadedImg'} src={preview} alt='user uploaded image'/>): ""}
              {isCapturing && <video id="cameraPreview" autoPlay></video>}
              {(!preview && !isCapturing) && 
                <div className='lhsInfo'>
                  <div className='infoIcon'>
                    <span><img alt='camImgIcon' src={'/camImgIcon.png'}/></span>
                  </div>
                  <div className='infoTxt'>Uploaded file would show up here</div>
                </div>}
            </div>
            <div className='rhs'>
              {extractedText && <pre>{extractedText}</pre>}
              {errMsg && <div className='errMsg'>{errMsg}</div>}
              {isLoading && <img className='loaderIcon' src={'/loader.gif'} alt='loader'/>}
              {(!extractedText && !errMsg && !isLoading) && 
                <div className='rhsInfo'>
                  <div className='infoIcon'>
                    <span><img alt='textIcon' src={'/textIcon.svg'}/></span>
                  </div>
                  <div className='infoTxt'>Extracted text would show up here</div>
                </div>}
            </div>
          </div>
        </div>
        <div className='content2'>
          <div className='lhs' style={{gap: '10px'}}>
            {base64Image && 
              <>
                <div className={`extractBtn ${disableExtraction ? 'disabled' : ''}`} onClick={disableExtraction ? null : extractTextFromImage}>
                  Extract Text
                </div>
                {imgFile?.type === 'application/pdf' && <div className='pageNoDiv'>
                    <span>from Page </span>
                    <input type="number" id="pageNumberInput" min="1" value={selectedPdfPageNo} onChange={(e) => {
                      setSelectedPdfPageNo(e.target.value);
                      setDisableExtraction(false);
                    }}></input>
                  </div>}
                <div className={`translationDiv ${isLoading ? 'disabled' : ''}`}>
                  <span> in </span> 
                  <select id="extLanguage" name="extLanguage" value={selectedLang} onChange={handleLangChange}>
                      <option className="languageOption default" value="">Original language</option>
                      {languageOptions}
                  </select> 
                </div>
              </>
            }
            {isCapturing && <div className={`captureBtn`} onClick={capturePhoto}>Capture Photo</div>}
          </div>
          <div className='rhs'>
            {(!(extractedText === "" || extractedText.trim() === "No text found")) && 
              // <a 
              //   className='downloadBtn'
              //   href={blobUrl || "#"}
              //   download={imgFile ? `${imgFile.name.replace(/\.[^/.]+$/, '')}_extracted.txt` : 'photo_extracted.txt'}
              //   onClick={() => {
              //     //event tracker
              //     gtag('event', 'download_clicked', { event_category: 'Button', event_label: 'Download Text' });
                  
              //     //download toast
              //     showDownloadToast();
              //   }}
              // >
              //     Download Text
              // </a>
              <div 
                className='downloadBtn'
                onClick={async () => {
                  //copying extracted text to clipboard
                  await navigator.clipboard.writeText(extractedText);
                  
                  //download toast
                  showCopiedToast();
                }}
              >
                  Copy Text
              </div>
            }
          </div>
        </div>
        <div className='content2Mobile'>
            {(!(extractedText === "" || extractedText.trim() === "No text found")) ?
              <div className='rhs'>
                {/* <a 
                  className='downloadBtn'
                  href={blobUrl || "#"}
                  download={imgFile ? `${imgFile.name.replace(/\.[^/.]+$/, '')}_extracted.txt` : 'photo_extracted.txt'}
                  onClick={() => {
                    //event tracker
                    gtag('event', 'download_clicked', { event_category: 'Button', event_label: 'Download Text' });
                    
                    //download toast
                    showDownloadToast();
                  }}
                >
                    Download Text
                </a> */}
                <div 
                  className='downloadBtn'
                  onClick={async () => {
                    //copying extracted text to clipboard
                    await navigator.clipboard.writeText(extractedText);
                    
                    //download toast
                    showCopiedToast();
                  }}
                >
                    Copy Text
                </div>
              </div>
              :
              <div className='lhs'>
                {base64Image && 
                  <>
                    <div className={`extractBtn ${disableExtraction ? 'disabled' : ''}`} onClick={disableExtraction ? null : extractTextFromImage}>
                      Extract Text
                    </div>
                    <div className='extractSettings'>
                      {imgFile?.type === 'application/pdf' && <div className='pageNoDiv'>
                        <span>from Page </span>
                        <input type="number" id="pageNumberInput" min="1" value={selectedPdfPageNo} onChange={(e) => {
                          setSelectedPdfPageNo(e.target.value);
                          setDisableExtraction(false);
                        }}></input>
                      </div>}
                      <div className={`translationDiv ${isLoading ? 'disabled' : ''}`}>
                        <span> in </span> 
                        <select id="extLanguageMobile" name="extLanguageMobile" value={selectedLang} onChange={handleLangChange}>
                            <option className="languageOption default" value="">Original language</option>
                            {languageOptions}
                        </select> 
                      </div>
                    </div>
                  </>
                }
                {isCapturing && <div className={`captureBtn`} onClick={capturePhoto}>Capture Photo</div>}
              </div>
            }
        </div>
        {showFeedbackPopup && <FeedbackPopup closeFeedbackPopup={() => setShowFeedbackPopup(false)}/>}
      </div>}
      {(user !== undefined) && !isUserLoggedIn && <><div className='mainDiv newUser'>
        <div className="heroSection">
          <Navbar setShowFeedbackPopup={setShowFeedbackPopup} isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} isNewUser={true}/>     
          <div className='intro'>
            <div className='introWrapper'>
              <h1 className='headline'>Copy Text from PDF in 1 click!<br/>AI powered PDF to Text Converter</h1>
              <h2 className='sub-headline'>
                Extract text from any scanned PDF or image instantly. 10x your productivity!
              </h2>
              <div className='ctaContainer'>
                <div className='wrapper'>
                  <div className='ctaBtn' onClick={initiateLogin}>Try for free</div>
                  <div className='extensionLink'>Need to extract table? <span className='linkTxt'><a style={{fontWeight: 550, color: 'black'}} href="https://workspace.google.com/marketplace/app/convert_pdf_to_google_sheets/687083288287" target="_blank" rel="noopener">
                            Convert PDF to Google Sheets
                          </a></span></div>
                  <div className='infoTxt'><span>Powered by OpenAI </span><img src={'/openAiIcon.png'}/><span> & Google Vision </span><img className='googleIcon' src={'/googleVisionApiIcon.png'}/></div>
                </div>
              </div>
            </div>
          </div>
          <div className='usersDiv'>Neatly Formatted Output . 100% Ad-Free . 100% Privacy . Camera Support . Instant Translations (100+ languages)</div>
        </div>
        <div className='content2Mobile'>
            {(!(extractedText === "" || extractedText.trim() === "No text found")) ?
              <div className='rhs'>
                {/* <a 
                  className='downloadBtn'
                  href={blobUrl || "#"}
                  download={imgFile ? `${imgFile.name.replace(/\.[^/.]+$/, '')}_extracted.txt` : 'photo_extracted.txt'}
                  onClick={() => {
                    //event tracker
                    gtag('event', 'download_clicked', { event_category: 'Button', event_label: 'Download Text' });
                    
                    //download toast
                    showDownloadToast();
                  }}
                >
                    Download Text
                </a> */}
                <div 
                  className='downloadBtn'
                  onClick={async () => {
                    //copying extracted text to clipboard
                    await navigator.clipboard.writeText(extractedText);
                    
                    //download toast
                    showCopiedToast();
                  }}
                >
                    Copy Text
                </div>
              </div>
              :
              <div className='lhs'>
                {base64Image && 
                  <div className={`extractBtn ${extractedText || isLoading ? 'disabled' : ''}`} onClick={extractedText || isLoading ? null : extractTextFromImage}>Extract Text</div>}
                {isCapturing && <div className={`captureBtn`} onClick={capturePhoto}>Capture Photo</div>}
              </div>
            }
        </div>
        {showFeedbackPopup && <FeedbackPopup closeFeedbackPopup={() => setShowFeedbackPopup(false)}/>}
      </div>
      <div className='newUserDemoContainer'>
        <div className='wrapper'>
         <img src={'/NewUserDemo.gif'} alt='Demo of the AI powered PDF to text converter showing how to copy text from PDF.'/>
        </div>
      </div>
      </>
      }
      {(user !== undefined) && (
        <>
          <UseCases />
          <WhythisApp />
          <HowItWorks />
          <FAQ />
          <FooterComponent setShowFeedbackPopup={setShowFeedbackPopup}/>
        </>
      )}
      {/* {(user === undefined) && (
        <>
          <div className='pageLoader'><img className='loaderIcon' src={'/PageLoader.gif'} alt='loader'/></div>
        </>
      )} */}
    </div>
  );
}

export default Home;
